// Code block component with copy button for chat messages
import { isValidElement } from 'react'
import { Copy, Check } from 'lucide-react'

interface CodeBlockProps {
  inline?: boolean
  children: React.ReactNode
  copiedCode: string | null
  onCopy: (code: string) => void
  className?: string
  [key: string]: any
}

/**
 * Recursively extract plain text from a React children tree.
 *
 * rehype-highlight wraps syntax-highlighted code tokens in nested `<span>` elements, so
 * `children` here is a mix of plain strings and React elements (not just strings). Naively
 * calling `String(children)` on that array coerces each element via its default object
 * `toString()`, producing "[object Object]" per highlighted token instead of the token's
 * text - which is exactly what corrupted output pasted via the copy button. Walk the tree
 * and concatenate only the actual text content instead.
 */
function extractPlainText(node: React.ReactNode): string {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(extractPlainText).join('')
  if (isValidElement(node)) return extractPlainText((node.props as { children?: React.ReactNode }).children)
  return ''
}

export function CodeBlock({
  inline,
  children,
  copiedCode,
  onCopy,
  className,
  ...props
}: CodeBlockProps) {
  const codeString = extractPlainText(children).replace(/\n$/, '')
  
  const hasNewlines = codeString.includes('\n')
  const hasLanguage = className?.startsWith('language-')
  const isInline = inline || (!hasLanguage && !hasNewlines)
  
  if (isInline) {
    return <code className="px-1.5 py-0.5 bg-muted rounded text-sm font-mono" {...props}>{children}</code>
  }
  
  const language = className?.replace('language-', '') || 'code'
  
  // Use prototype-style narrow code pane for better UX
  // Matches the CodeGenerationBox style from thinking mode prototype
  return (
    <div className="relative group my-4 not-prose border border-border rounded-lg overflow-hidden bg-card w-full max-w-full">
      {/* Filename header - matches prototype style exactly */}
      <div className="bg-muted/50 px-3 py-1.5 text-xs border-b border-border flex items-center justify-between">
        <span className="text-sm">{language !== 'code' ? language : 'code'}</span>
        <button 
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-muted rounded"
          onClick={() => onCopy(codeString)}
          title="Copy code"
        >
          {copiedCode === codeString ? (
            <Check className="w-3.5 h-3.5 text-green-500" />
          ) : (
            <Copy className="w-3.5 h-3.5 text-muted-foreground" />
          )}
        </button>
      </div>
      {/* Code content - wrap long lines (diffs / soul proposals) instead of horizontal scroll */}
      <div className="bg-background p-4 font-mono text-xs overflow-y-auto overflow-x-hidden w-full min-w-0" style={{ maxHeight: '200px' }}>
        <pre className="whitespace-pre-wrap break-words text-foreground m-0 w-full min-w-0 [overflow-wrap:anywhere]">
          <code className="text-foreground block whitespace-pre-wrap break-words [overflow-wrap:anywhere]" {...props}>{children}</code>
        </pre>
      </div>
    </div>
  )
}


